import { Alert, Box, Button, CircularProgress, Divider, Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import PageContainer from '../layout/PageContainer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client/react';
import { Batch, CombinedCalculatedItemResult } from '../../models/Batch';
import { GET_PROCESS_BATCHES_RESULT_QUERY } from '../../queries/BatchQueries';
import { InventoryItem } from '../../models/InventoryItem';
import {
  DataGrid,
  GridColDef,
  gridClasses
} from '@mui/x-data-grid';
import dayjs from 'dayjs';

const INITIAL_PAGE_SIZE = 100;

export default function ShoppingList() {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const [rowsState, setRowsState] = useState<{
    rows: CombinedCalculatedItemResult[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const [getBatchesForProcessingByIds] = useLazyQuery(
    GET_PROCESS_BATCHES_RESULT_QUERY);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [batches, setBatches] = useState<Batch[]>([]);

  const loadData = useCallback(async () => {
    setError(null);

    const unparsedBatchIds = searchParams.get('batchIds') || '';
    const batchIds = unparsedBatchIds.split(',').map(id => parseInt(id, 10));

    const unparsedItemIds = searchParams.get('itemIds') || '';
    const itemIds = unparsedItemIds.split(',').map(id => parseInt(id, 10));

    if (batchIds.length > 0 &&
      itemIds.length > 0) {
      setIsLoading(true);

      try {
        const { data, error: getBatchesByIdsError } = await getBatchesForProcessingByIds(
          { variables: { ids: batchIds } }
        );

        setIsLoading(false);
        setError(null);

        if (getBatchesByIdsError) {
          setError(getBatchesByIdsError);
        }

        if (data?.processedBatchesResult) {
          setBatches(data.processedBatchesResult.batches);

          const filteredCombinedCalculatedItemResults = (data.processedBatchesResult.combinedCalculatedItemResults || []).filter(
            (itemResult: CombinedCalculatedItemResult) => itemIds.includes(itemResult.item.id));

          setRowsState({
            rows: filteredCombinedCalculatedItemResults || [],
            rowCount: filteredCombinedCalculatedItemResults.length || 0,
          });
        } else {
          setError({ message: 'No batches found' } as Error);
        }
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getBatchesForProcessingByIds, searchParams]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBack = useCallback(() => {
    navigate(`/dashboard/batches/process?batchIds=${batches.map(batch => batch.id).join(',')}`);
  }, [batches, navigate]);

  const initialState = useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'itemName',
        headerName: 'Item',
        display: 'flex',
        flex: 1.5,
        valueGetter: (_, row) => row.item?.name
      },
      {
        field: 'totalQuantity',
        headerName: 'Total Qty Needed (lbs)',
        type: 'number',
        display: 'flex',
        flex: 1.25,
        valueFormatter: (value: number) => value.toFixed(3)
      },
      {
        field: 'totalQuantityOnHand',
        headerName: 'Total QOH (lbs)',
        type: 'number',
        display: 'flex',
        flex: 1,
        valueFormatter: (value: number) => value.toFixed(3)
      },
      {
        field: 'totalRemainingQuantityOnHand',
        headerName: 'ROH (lbs)',
        type: 'number',
        display: 'flex',
        flex: .75,
        valueFormatter: (value: number) => value.toFixed(3)
      },
      {
        field: 'itemInventoryItems',
        headerName: 'Inventory Items',
        sortable: false,
        flex: 5,
        minWidth: 625,
        valueGetter: (value, row: CombinedCalculatedItemResult) => {
          return row.item?.inventoryItems ? row.item.inventoryItems.map(item => `${item.lotNumber || 'N/A'}; ${item.vendor?.name || 'N/A'}; ${(item.quantityOnHand || 0).toFixed(3)} lbs; ${item.quantityOrdered.toFixed(3)} lbs; ${dayjs(item.dateOrdered).format('M/D/YYYY') || 'N/A'}`).join('\n') : '';
        },
        renderCell: (params) => {
          return (
            <Table aria-label="dense table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} component="th" scope="row">Lot #</TableCell>
                  <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} component="th" scope="row">Vendor</TableCell>
                  <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} component="th" scope="row" align="right">QOH (lbs)</TableCell>
                  <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} component="th" scope="row" align="right">Ordered Qty (lbs)</TableCell>
                  <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} component="th" scope="row">Ordered Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(params.row.item.inventoryItems || []).map((inventoryItem: InventoryItem) => (
                  <TableRow
                    key={inventoryItem.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }}>
                      {inventoryItem.lotNumber || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }}>
                      {inventoryItem.vendor?.name}
                    </TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} align="right">
                      {(inventoryItem.quantityOnHand || 0).toFixed(3)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }} align="right">
                      {inventoryItem.quantityOrdered.toFixed(3)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '20%' }}>
                      {dayjs(inventoryItem.dateOrdered).format('M/D/YYYY')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          );
        }
      }
    ],
    [],
  );

  const renderShow = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return (batches || []).length > 0 ?
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <Box sx={{ height: '50vh' }}>
          <DataGrid
            density="compact"
            rows={rowsState.rows}
            columns={columns}
            getRowId={(row) => row.item.id}
            pagination
            disableRowSelectionOnClick
            loading={isLoading}
            initialState={initialState}
            showToolbar
            pageSizeOptions={[INITIAL_PAGE_SIZE]}
            getRowHeight={() => 'auto'}
            sx={{
              '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
              '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
              '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
              [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                outline: 'transparent',
              },
              [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
              {
                outline: 'none',
              },
              [`& .${gridClasses.row}:hover`]: {
                cursor: 'pointer',
              }
            }}
            slotProps={{
              loadingOverlay: {
                variant: 'circular-progress',
                noRowsVariant: 'circular-progress',
              },
              baseIconButton: {
                size: 'small',
              },
            }}
          />
        </Box>
        <Divider sx={{ my: 3 }} />
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
        </Stack>
      </Box> : <></>;
  }, [isLoading, error, batches, rowsState.rows, columns, initialState, handleBack]);

  const pageTitle = 'Shopping List';

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Maths', path: `/dashboard/batches/process?batchIds=${batches.map(batch => batch.id).join(',')}` },
        { title: pageTitle },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>{renderShow}</Box>
    </PageContainer>
  );
}